/**
 * REST client for the /api routes (packages/server/src/api/boards.ts).
 * DTOs come from @kola/shared. Dev identity rides the `x-dev-user` header.
 */
import {
  MAX_ASSET_BYTES,
  type BoardAgentRequest,
  type BoardAgentResponse,
  MAX_AUDIO_BYTES,
  MAX_FILE_BYTES,
  type UploadFileAssetResponse,
  type AiFunctionKey,
  type AiModelCatalogResponse,
  type AiModelEntry,
  type AiModelEntryDeletedResponse,
  type AiModelEntryInput,
  type AiModelListResponse,
  type AiModelPersonalSettingResponse,
  type AiModelSettingClearedResponse,
  type AiModelSettingResponse,
  type TextKind,
  type TextKindDeletedResponse,
  type TextKindInput,
  type TextKindsResponse,
  type ArtDirection,
  type BoardStatus,
  type BoardSummary,
  type BoardTaskAssignee,
  type ChangeBoardStatusResponse,
  type CreateDebugReportResponse,
  type DebugReportRequest,
  type DebugReportsResponse,
  type DeleteDebugReportResponse,
  type SetShootingOwnersRequest,
  type ShootingOwnersResponse,
  type StoryboardReviewResponse,
  type MarkNotificationsReadRequest,
  type MeResponse,
  type NotificationsResponse,
  type PresenterPhoto,
  type PresenterPhotosResponse,
  type PresenterPhotoUpdateInput,
  type PresenterPhotoUploadResponse,
  type TeamMember,
  type ThumbShotInfo,
  type AuthModeResponse,
  KOLA_API_CONTRACT,
  KOLA_CLIENT_HEADER,
  formatKolaClientHeader,
  type BoardNameSuggestionRequest,
  type ImportStepRequest,
  type BoardNameSuggestionResponse,
  type CreateBoardRequest,
  type UpdateBoardRequest,
  type SetShortsCrewRequest,
  type ShortsCrewResponse,
} from "@kola/shared";

import { clientVersion, debugLog, describeError } from "./debug-log.js";
import { announceBoardsChanged, apiUrl, getHost } from "./host.js";
import { identityTransportValue } from "./identity.js";
import { noteAuthMode, noteClientOutdated } from "./server-info.js";

/** Upload caps — the SHARED constants the server enforces as bodyLimits (413). */
export { MAX_ASSET_BYTES, MAX_AUDIO_BYTES };

export function analyzeCommentRevision(boardId: string, threadId: string): Promise<import("@kola/shared").RevisionAnalysis> {
  return apiFetch(`/api/boards/${encodeURIComponent(boardId)}/comments/${encodeURIComponent(threadId)}/analyze-revision`, { method: "POST" });
}

/** Standalone experiments: these endpoints never write to a board document. */
export function analyzeLabIntent(input: import("@kola/shared").LabIntentRequest): Promise<import("@kola/shared").LabIntentResponse> {
  return apiFetch("/api/storyboard-lab/intent", { method: "POST", body: JSON.stringify(input) });
}

export function segmentLabScript(input: import("@kola/shared").LabSegmentRequest): Promise<import("@kola/shared").LabSegmentResponse> {
  return apiFetch("/api/storyboard-lab/segment", { method: "POST", body: JSON.stringify(input) });
}

export function classifyLabChunks(input: import("@kola/shared").LabClassifyRequest): Promise<import("@kola/shared").LabClassifyResponse> {
  return apiFetch("/api/storyboard-lab/classify", { method: "POST", body: JSON.stringify(input) });
}

export function analyzeLabDelivery(input: import("@kola/shared").LabDeliveryRequest): Promise<import("@kola/shared").LabDeliveryResponse> {
  return apiFetch("/api/storyboard-lab/delivery", { method: "POST", body: JSON.stringify(input) });
}

export function reviewLabEdit(input: import("@kola/shared").LabReviewRequest): Promise<import("@kola/shared").LabReviewResponse> {
  return apiFetch("/api/storyboard-lab/review", { method: "POST", body: JSON.stringify(input) });
}

/**
 * A non-2xx /api response, carrying the two things a caller needs to say
 * something useful to the user: the HTTP `status` and the server's machine
 * `code` (the `error` field of our error bodies — "translation-not-configured",
 * "already-running", …). `message` keeps the original human-readable
 * "METHOD /path failed (status: code)" text, so callers that only surface
 * `err.message` are unaffected.
 */

export class ApiError extends Error {
  readonly status: number;
  /** The response body's `error` code, or "" for a non-JSON/empty body. */
  readonly code: string;
  /**
   * The parsed error body (`undefined` when it was not JSON). Some errors
   * carry more than a code: a 409 `already-running` names the live job's
   * `jobId`, so the caller can attach to the run that IS happening instead of
   * only being told "no".
   */
  readonly body: unknown;

  constructor(message: string, status: number, code: string, body?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.body = body;
  }
}

/**
 * Identity transport for the configured auth mode and shell (core/host.ts):
 *  - dev/iap: the `x-dev-user` header (the server ignores it under IAP);
 *  - firebase, web shell: nothing — the kola_session cookie rides automatically;
 *  - firebase, embedded shell: `Authorization: Bearer <firebase id token>`
 *    from the host (the cookie is same-origin and never leaves the Kola
 *    origin, so the desktop app proves itself on every request — the
 *    server's Bearer path). `forceFresh` asks for a token that bypasses the
 *    SDK's cache: the one retry after a 401, for a token that expired before
 *    the SDK noticed.
 * An embedded shell also announces its build (`x-kola-client`) so the server
 * can log client/server skew and refuse a client below its contract floor
 * (shared/contract.ts).
 */
async function authHeaders(forceFresh = false): Promise<Record<string, string>> {
  const host = getHost();
  const headers: Record<string, string> =
    host === null
      ? {}
      : { [KOLA_CLIENT_HEADER]: formatKolaClientHeader(clientVersion(), KOLA_API_CONTRACT) };
  // product: the product mode carries a bearer token like firebase.
  if (authMode !== "firebase" && authMode !== "product") {
    headers["x-dev-user"] = identityTransportValue();
    return headers;
  }
  if (host === null) return headers;
  const token = await host.getIdToken(forceFresh);
  if (token !== null) headers["authorization"] = `Bearer ${token}`;
  return headers;
}

/**
 * fetch() against the server (relative in the web shell, absolute when
 * embedded) with the identity transport above and the firebase-mode 401
 * recovery, retried ONCE: the web shell re-mints a session cookie that
 * expired (7-day TTL) or was invalidated by a SESSION_SECRET rotation; the
 * embedded shell asks the host for a fresh ID token. Dev/iap modes never
 * retry — a 401 there is a real answer. A 426 means the server's contract
 * floor has moved past this build (shared/contract.ts): it is recorded so
 * the shell can say "update the app", and passed through unchanged.
 */
async function authedFetch(path: string, init: RequestInit): Promise<Response> {
  const url = apiUrl(path);
  const send = async (forceFresh: boolean): Promise<Response> =>
    fetch(url, {
      ...init,
      headers: {
        ...(init.headers as Record<string, string> | undefined),
        ...(await authHeaders(forceFresh)),
      },
    });
  let response: Response;
  try {
    response = await send(false);
  } catch (err) {
    // No response at all — offline, DNS, a blocked tunnel, an aborted tab.
    // The one class of failure a status-code log line can never show.
    debugLog.error("api", `${init.method ?? "GET"} ${path} failed: ${describeError(err).message}`);
    throw err;
  }
  if (response.status === 426) {
    void response
      .clone()
      .json()
      .then(noteClientOutdated)
      .catch(() => noteClientOutdated(undefined));
    return response;
  }
  if (response.status !== 401 || (authMode !== "firebase" && authMode !== "product")) return response;
  if (getHost() !== null) return send(true);
  const { ensureSession } = await import("./auth-session.js");
  if (!(await ensureSession())) return response;
  return send(false);
}

/**
 * A write that moves board METADATA (name, stage, dates, archived state):
 * once it lands, tell the host so its other views refetch (host.ts
 * `boardsChanged`). Board content needs none of this — Yjs carries it.
 */
async function boardsWrite<T>(write: Promise<T>): Promise<T> {
  const result = await write;
  announceBoardsChanged();
  return result;
}

async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await authedFetch(path, {
    ...init,
    headers: {
      // Only announce a JSON body when there IS one. Fastify rejects a request
      // that declares `content-type: application/json` but sends an empty body
      // (FST_ERR_CTP_EMPTY_JSON_BODY → 400) — which is exactly our bodyless
      // DELETEs (translate cancel/dismiss). Setting it unconditionally made
      // every Dismiss click 400, and the panel's handler only swallows 409, so
      // the "Finished translating…" banner never cleared and toasted an error.
      ...(init.body === undefined || init.body === null
        ? {}
        : { "content-type": "application/json" }),
      ...init.headers,
    },
  });
  if (!response.ok) {
    let detail = "";
    let body: unknown;
    try {
      body = await response.json();
      detail = (body as { error?: string }).error ?? "";
    } catch {
      // non-JSON error body — status alone is enough
    }
    // Debug log (core/debug-log.ts): a 5xx or a refused identity is an error;
    // any other 4xx is usually the caller's expected answer (a 409 "already
    // running", a 404 unfurl) and only worth a warn line.
    const level =
      response.status >= 500 || response.status === 401 || response.status === 403
        ? "error"
        : "warn";
    debugLog[level](
      "api",
      `${init.method ?? "GET"} ${path} → ${response.status}${detail ? ` ${detail}` : ""}`,
      body === undefined ? undefined : JSON.stringify(body),
    );
    throw new ApiError(
      `${init.method ?? "GET"} ${path} failed (${response.status}${detail ? `: ${detail}` : ""})`,
      response.status,
      detail,
      body,
    );
  }
  return (await response.json()) as T;
}

export function fetchMe(): Promise<MeResponse> {
  return apiFetch<MeResponse>("/api/me");
}

export type AuthModeValue = MeResponse["authMode"];

/**
 * The server's auth mode — gates the identity transports above and the app
 * shell's sign-in flow (App.tsx). Until /api/auth/mode answers, the Vite
 * build mode is the best guess: a dev build talks to the dev server; a
 * production build authenticates with Firebase. Getting the guess wrong is
 * benign — App.tsx awaits the real answer before rendering any route.
 */
let authMode: AuthModeValue = import.meta.env.DEV ? "dev" : "firebase";

/** The auth mode as last resolved — the socket (core/session.ts) picks its token by it. */
export function getAuthMode(): AuthModeValue {
  return authMode;
}

/**
 * Resolve the server's real auth mode once at app start. Unauthenticated on
 * purpose (the server exposes it outside the identity hook): in firebase
 * mode nothing else on /api answers before sign-in. The same answer carries
 * the server's wire contract + version (shared/contract.ts), recorded in
 * core/server-info.ts for the embedded shell's outdated-client gate and the
 * version badge.
 */
export async function fetchAuthMode(): Promise<AuthModeValue> {
  try {
    const response = await fetch(apiUrl("/api/auth/mode"));
    if (response.ok) {
      const info = (await response.json()) as AuthModeResponse;
      authMode = info.mode;
      noteAuthMode(info);
    }
  } catch {
    // Keep the build-mode guess; a later navigation can retry.
  }
  return authMode;
}

/** Default excludes archived; `archived: true` returns archived-only. */
export function listBoards(archived = false): Promise<BoardSummary[]> {
  return apiFetch<BoardSummary[]>(`/api/boards${archived ? "?archived=1" : ""}`);
}

/**
 * Create a project (F-135). Not just a name: the server refuses a board with
 * no script finish date or no writer, and materializes the Scripting task
 * for the assignees, due on that date.
 */
export function createBoard(input: CreateBoardRequest): Promise<BoardSummary> {
  return boardsWrite(
    apiFetch<BoardSummary>("/api/boards", {
      method: "POST",
      body: JSON.stringify(input),
    }),
  );
}

/**
 * F-150: propose one step of a file import (split / translate / align) as a
 * plan — answered like an agent turn, applied like one.
 */
export function runImportStep(boardId: string, request: ImportStepRequest): Promise<BoardAgentResponse> {
  return apiFetch<BoardAgentResponse>(`/api/boards/${encodeURIComponent(boardId)}/import-step`, {
    method: "POST",
    body: JSON.stringify(request),
  });
}

/** F-147: English keywords for a board created from an imported file. */
export function suggestBoardName(input: BoardNameSuggestionRequest): Promise<BoardNameSuggestionResponse> {
  return apiFetch<BoardNameSuggestionResponse>("/api/boards/name-suggestion", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function renameBoard(id: string, name: string): Promise<BoardSummary> {
  return boardsWrite(
    apiFetch<BoardSummary>(`/api/boards/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify({ name }),
    }),
  );
}

/**
 * F-141: move a Shorts week's plan — the shoot day, the edit deadline, or
 * any slot's title, release day, state or URL — through the same PATCH the
 * script deadline uses. The server applies the slot patches and answers with
 * the whole board, so the caller feeds the answer straight into its summary.
 */
export function updateBoardPlan(
  id: string,
  patch: Pick<UpdateBoardRequest, "shootDate" | "editDueDate" | "shorts">,
): Promise<BoardSummary> {
  return boardsWrite(
    apiFetch<BoardSummary>(`/api/boards/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),
  );
}

/**
 * F-137: move the board's script deadline (`YYYY-MM-DD`). The server carries
 * the new date to the Scripting task on the rainvic board and to the Projects
 * overlay, so this one call is the whole edit.
 */
export function setBoardScriptFinishDate(id: string, scriptFinishDate: string): Promise<BoardSummary> {
  return boardsWrite(
    apiFetch<BoardSummary>(`/api/boards/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify({ scriptFinishDate }),
    }),
  );
}

export function archiveBoard(id: string): Promise<BoardSummary> {
  return boardsWrite(
    apiFetch<BoardSummary>(`/api/boards/${encodeURIComponent(id)}/archive`, {
      method: "POST",
    }),
  );
}

export function restoreBoard(id: string): Promise<BoardSummary> {
  return boardsWrite(
    apiFetch<BoardSummary>(`/api/boards/${encodeURIComponent(id)}/restore`, {
      method: "POST",
    }),
  );
}

export function getBoard(id: string): Promise<BoardSummary> {
  return apiFetch<BoardSummary>(`/api/boards/${encodeURIComponent(id)}`);
}

/**
 * Move a board to another pipeline stage. At least one assignee is required
 * by design: the transition materializes a task for whoever owns the new
 * stage on the shared rainvic-tool task board. `taskCreated: false` +
 * `taskError` in the response means the stage DID change but the task write
 * failed — surface it, don't roll back.
 */
export function changeBoardStatus(
  id: string,
  status: BoardStatus,
  assignees: BoardTaskAssignee[],
): Promise<ChangeBoardStatusResponse> {
  return boardsWrite(
    apiFetch<ChangeBoardStatusResponse>(`/api/boards/${encodeURIComponent(id)}/status`, {
      method: "POST",
      body: JSON.stringify({ status, assignees }),
    }),
  );
}

/* ------------------------------------------------------------------ */
/* Storyboard sign-off (F-126) + its auto-advance to Shooting (F-133)  */
/* ------------------------------------------------------------------ */

/**
 * Sign the storyboard off. The server does not just record it: an approval
 * moves the board to Shooting on its own and writes the stage task, and the
 * response says so (`autoAdvanced`) — the caller should tell the user that
 * happened rather than leaving them to notice the card moved columns.
 *
 * `assignees` overrides the workspace's shooting-owners preset for this one
 * board; leaving it off is the normal case.
 */
export function approveStoryboard(
  boardId: string,
  options: { note?: string; assignees?: BoardTaskAssignee[] } = {},
): Promise<StoryboardReviewResponse> {
  return boardsWrite(
    apiFetch<StoryboardReviewResponse>(`/api/boards/${encodeURIComponent(boardId)}/storyboard/approve`, {
      method: "POST",
      body: JSON.stringify(options),
    }),
  );
}

/**
 * Send the storyboard back to the artists. `note` is required — the server
 * rejects an empty one, because a rejection with no reason is how a review
 * loop stalls.
 */
export function rejectStoryboard(
  boardId: string,
  note: string,
  assignees?: BoardTaskAssignee[],
): Promise<StoryboardReviewResponse> {
  return boardsWrite(
    apiFetch<StoryboardReviewResponse>(`/api/boards/${encodeURIComponent(boardId)}/storyboard/reject`, {
      method: "POST",
      body: JSON.stringify({ note, ...(assignees ? { assignees } : {}) }),
    }),
  );
}

/* ------------------------------------------------------------------ */
/* Admin: who owns a board once it reaches Shooting (F-133)            */
/* ------------------------------------------------------------------ */

export function fetchShootingOwners(): Promise<ShootingOwnersResponse> {
  return apiFetch<ShootingOwnersResponse>("/api/admin/shooting-owners");
}

/** Save the preset. An empty list is a real save: "the approver owns it". */
export function saveShootingOwners(
  assignees: BoardTaskAssignee[],
): Promise<ShootingOwnersResponse> {
  const body: SetShootingOwnersRequest = { assignees };
  return apiFetch<ShootingOwnersResponse>("/api/admin/shooting-owners", {
    method: "PUT",
    body: JSON.stringify(body),
  });
}

/* ------------------------------------------------------------------ */
/* The standing Shorts crew (F-142) — read by any member, set by admin */
/* ------------------------------------------------------------------ */

export function fetchShortsCrew(): Promise<ShortsCrewResponse> {
  return apiFetch<ShortsCrewResponse>("/api/shorts-crew");
}

/** Save the crew. An empty role is a real save: that stage's picker opens blank. */
export function saveShortsCrew(crew: SetShortsCrewRequest): Promise<ShortsCrewResponse> {
  return apiFetch<ShortsCrewResponse>("/api/admin/shorts-crew", {
    method: "PUT",
    body: JSON.stringify(crew),
  });
}

/**
 * Roster for the status-change assignee picker AND the F-54 @mention
 * autocomplete (shared rainvic-tool team).
 */
export function fetchTeam(): Promise<TeamMember[]> {
  return apiFetch<TeamMember[]>("/api/team");
}

/* ------------------------------------------------------------------ */
/* Notification inbox (F-110)                                          */
/* ------------------------------------------------------------------ */

/** This user's inbox, newest first, plus the unread count for the badge. */
export function fetchNotifications(limit?: number): Promise<NotificationsResponse> {
  const query = limit === undefined ? "" : `?limit=${encodeURIComponent(String(limit))}`;
  return apiFetch<NotificationsResponse>(`/api/notifications${query}`);
}

/**
 * Mark rows read. Omit `ids` to clear the whole inbox; an EMPTY array marks
 * nothing (the server draws the same distinction). Returns the new unread
 * count, so the badge does not need a follow-up fetch.
 */
export function markNotificationsRead(ids?: readonly string[]): Promise<{ unread: number }> {
  const body: MarkNotificationsReadRequest = ids === undefined ? {} : { ids: [...ids] };
  return apiFetch<{ unread: number }>("/api/notifications/read", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/* ------------------------------------------------------------------ */
/* Assets (F-30)                                                       */
/* ------------------------------------------------------------------ */

/** POST /api/boards/:boardId/assets — 201 response body. */
export interface AssetUploadResult {
  assetId: string;
  /** Intrinsic pixel width, parsed server-side. */
  w: number;
  /** Intrinsic pixel height, parsed server-side. */
  h: number;
}

/**
 * Upload raw image bytes (F-30). The body is the file itself; the server
 * sniffs magic bytes and rejects anything that isn't a real
 * png/jpeg/gif/webp (415). Returns the asset id + intrinsic size, ready for
 * `createImageElement` (images are always elements now — F-30).
 */
export async function uploadAsset(boardId: string, file: Blob): Promise<AssetUploadResult> {
  const contentType = file.type.startsWith("image/") ? file.type : "image/png";
  const response = await authedFetch(`/api/boards/${encodeURIComponent(boardId)}/assets`, {
    method: "POST",
    headers: { "content-type": contentType },
    body: file,
  });
  if (!response.ok) {
    if (response.status === 415) throw new Error("That file is not a supported image (png/jpeg/gif/webp)");
    if (response.status === 413) throw new Error("Image is too large (20 MB max)");
    if (response.status === 403) throw new Error("You don't have permission to add images to this board");
    throw new Error(`Image upload failed (${response.status})`);
  }
  return (await response.json()) as AssetUploadResult;
}

/** 201 body of the audio upload — the sniffed type, not the header's claim. */
export interface AudioAssetUploadResult {
  assetId: string;
  contentType: string;
  size: number;
}

/**
 * Upload a BGM stem's bytes (F-126). Separate from `uploadAsset` because it
 * hits a separate route with its own cap (30 MB — Cloud Run's HTTP/1 request
 * ceiling, see MAX_AUDIO_BYTES) and because the accepted formats, and
 * therefore the error copy, are entirely different.
 */
export async function uploadAudioAsset(
  boardId: string,
  file: Blob,
): Promise<AudioAssetUploadResult> {
  const contentType = file.type.startsWith("audio/") ? file.type : "audio/mpeg";
  const response = await authedFetch(
    `/api/boards/${encodeURIComponent(boardId)}/audio-assets`,
    { method: "POST", headers: { "content-type": contentType }, body: file },
  );
  if (!response.ok) {
    if (response.status === 415) {
      throw new Error("That file is not supported audio (mp3/wav/m4a/aac/ogg/flac)");
    }
    if (response.status === 413) throw new Error("Audio file is too large (30 MB max)");
    if (response.status === 403) {
      throw new Error("You don't have permission to add music to this board");
    }
    throw new Error(`Audio upload failed (${response.status})`);
  }
  return (await response.json()) as AudioAssetUploadResult;
}

/**
 * Upload ANY file's bytes (F-135) — a video, an audio clip, a PDF, a project
 * file. The header is a transport detail (`application/octet-stream` always):
 * the server sniffs the bytes and answers with what they are, how the board
 * should present them, and a video's picture size when its header had one.
 * The route never 415s for what the bytes are — an unnameable file is stored
 * as an opaque download, which is the point.
 */
export async function uploadFileAsset(boardId: string, file: Blob): Promise<UploadFileAssetResponse> {
  const response = await authedFetch(`/api/boards/${encodeURIComponent(boardId)}/files`, {
    method: "POST",
    headers: { "content-type": "application/octet-stream" },
    body: file,
  });
  if (!response.ok) {
    if (response.status === 413) {
      throw new Error(`File is too large (${Math.round(MAX_FILE_BYTES / (1024 * 1024))} MB max)`);
    }
    if (response.status === 400) throw new Error("That file is empty");
    if (response.status === 403) throw new Error("You don't have permission to add files to this board");
    throw new Error(`File upload failed (${response.status})`);
  }
  return (await response.json()) as UploadFileAssetResponse;
}

/**
 * Fetch an asset's bytes with the caller's identity — the F-135 download
 * path. A plain link to the asset route would do in the web shell (the
 * cookie rides along), but the embedded shell has no cookie for the Kola
 * origin and Chromium ignores `download` on a cross-origin link anyway; so
 * both shells fetch the bytes through the authenticated channel and hand the
 * browser a blob to save (core/save-file.ts). The blob's `type` is the
 * server's recorded content type — what a nameless download is named after.
 */
export async function fetchAssetBlob(assetId: string): Promise<Blob> {
  const response = await authedFetch(`/api/assets/${encodeURIComponent(assetId)}`, { method: "GET" });
  if (!response.ok) {
    if (response.status === 404) throw new Error("That file is no longer on the server");
    if (response.status === 403) throw new Error("You don't have permission to download this file");
    throw new Error(`Download failed (${response.status})`);
  }
  return response.blob();
}

/**
 * URL that serves an uploaded asset's bytes (immutable, cacheable).
 * DEV ONLY: identity rides the `?user=` param because `<img>` tags can't
 * send the x-dev-user header. In production auth is cookie-based (the
 * kola_session cookie in firebase mode, IAP before it) — there the param
 * would be dead weight that writes the user's identity into browser history
 * and every access log (a needless PII trail) and splits the cache per
 * identity for the same immutable bytes, so it is omitted. The embedded
 * shell gets an absolute URL on the Kola origin; its `<img>`/`<audio>`
 * requests are authenticated by the desktop app's main process, which
 * injects the Bearer header on that origin's asset path (core/host.ts).
 */
export function assetUrl(assetId: string): string {
  const base = apiUrl(`/api/assets/${encodeURIComponent(assetId)}`);
  return authMode === "dev" ? `${base}?user=${identityTransportValue()}` : base;
}

/* ------------------------------------------------------------------ */
/* Link unfurl (F-31, docs/04 §7)                                      */
/* ------------------------------------------------------------------ */

/**
 * POST /api/boards/:id/unfurl response body — mirrors the server route's
 * `UnfurlResult` (packages/server/src/core/unfurl/unfurl.ts). The client patches
 * these fields into the link-card element payload (status:"ready"|"error").
 */
export interface UnfurlResult {
  title?: string;
  description?: string;
  /** Asset id of the stored thumbnail (bytes re-hosted in our bucket). */
  thumbnailAssetId?: string;
  /**
   * F-31(a): INTRINSIC pixel size of the STORED thumbnail, parsed from its
   * bytes (never a provider's claim) — drives the card's dynamic aspect ratio.
   * Present iff `thumbnailAssetId` is, on EVERY card with a thumbnail (not
   * only video ones).
   */
  thumbnailW?: number;
  thumbnailH?: number;
  /** Favicon URL for the favicon+title / domain-only rungs. */
  favicon?: string;
  /** Host of the requested URL — always present, the domain-only fallback. */
  domain: string;
  /**
   * F-31(b/c): `"video"` when a provider's oEmbed reported `type:"video"` —
   * the client renders thumbnail + title only. Absent = an ordinary link card.
   */
  kind?: "video";
  /** oEmbed `provider_name` ("YouTube"), when the card came from oEmbed. */
  provider?: string;
  status: "ready" | "error";
}

/**
 * Unfurl a pasted URL for a board (F-31): the server fetches the page under the
 * SSRF guard, parses OG/meta, re-hosts the thumbnail, and returns the card
 * fields. A blocked/unreachable target degrades to `status:"error"` inside a
 * 200 body; a malformed/non-http(s) URL is a 400 (rejected as a thrown Error).
 * Uses the same `x-dev-user` identity header as every other /api call.
 */
export function unfurl(boardId: string, url: string): Promise<UnfurlResult> {
  return apiFetch<UnfurlResult>(`/api/boards/${encodeURIComponent(boardId)}/unfurl`, {
    method: "POST",
    body: JSON.stringify({ url }),
  });
}

/**
 * `POST …/arrange-suggest` request/response (F-60 AI assist — packages/server/
 * src/api/arrange.ts). One cell = one over-length, un-punctuated sentence the
 * pure `Intl.Segmenter` path found nothing to split; `breaks` are character
 * offsets into that SAME cell's `text` (never replacement text — see the
 * server's split-sentence prompt module note), sliced client-side via
 * `features/arrange/segment.ts` `rawFromOffsets`. An empty `breaks` array
 * means the model found no confident place to cut.
 */
export interface ArrangeSuggestCell {
  id: string;
  text: string;
}

export interface ArrangeSuggestion {
  id: string;
  breaks: number[];
}

export interface ArrangeSuggestResponse {
  suggestions: ArrangeSuggestion[];
  tokensIn?: number;
  tokensOut?: number;
}

/**
 * Ask the server to suggest cut points for over-length, un-punctuated cells
 * (F-60's AI-assisted half). Bounded by the server's per-request cell/char
 * caps (ArrangeSuggestCell arrays are pre-sliced client-side to the same
 * defaults — `features/arrange/ai-suggest.ts` `selectAiBatch`); an oversized
 * request 400s rather than being silently truncated server-side.
 */
export function suggestArrangeSplit(
  boardId: string,
  cells: readonly ArrangeSuggestCell[],
): Promise<ArrangeSuggestResponse> {
  return apiFetch<ArrangeSuggestResponse>(
    `/api/boards/${encodeURIComponent(boardId)}/arrange-suggest`,
    { method: "POST", body: JSON.stringify({ cells }) },
  );
}

/* ------------------------------------------------------------------ */
/* Divide sections — group the script into titled sections             */
/* ------------------------------------------------------------------ */

/**
 * `POST …/sections-suggest` request/response (packages/server/src/api/
 * sections.ts). One row = one Original Script line, keyed by its immutable
 * row id; the response GROUPS those ids (never returns script text — see the
 * server's divide-sections prompt module note). `rowIds` may be
 * non-contiguous: a topic the script leaves and comes back to is ONE section.
 *
 * Two answers over the same rows (F-139): `sections` say WHAT the host is on
 * (the scene), `moods` say HOW each line is delivered — contiguous spans that
 * begin on the exact row the mood changed, independent of the sections. A
 * joke in the middle of a topic is a mood change inside one section.
 */
export interface SectionsSuggestRow {
  id: string;
  text: string;
}

export interface SuggestedSection {
  title: string;
  /**
   * The dominant mood of this section's rows — a server-derived summary kept
   * for clients built before `moods` existed; "" when the analysis carried no
   * moods. The row-level answer is `moods`.
   */
  mood: string;
  /** Row ids in script order — possibly split across several places. */
  rowIds: string[];
}

/** One contiguous run of rows in one mood; `rowIds[0]` is where it begins. */
export interface SuggestedMoodSpan {
  mood: string;
  rowIds: string[];
}

export interface SectionsSuggestResponse {
  sections: SuggestedSection[];
  /**
   * Mood spans in script order (F-139); empty when the model marked none.
   * Absent from a server built before F-139 — `toPreviewMoods` then derives
   * spans from the per-section moods.
   */
  moods?: SuggestedMoodSpan[];
  /** The model that actually analyzed (the panel's picker reports it). */
  model: string;
  tokensIn?: number;
  tokensOut?: number;
}

/**
 * Ask the server to divide the script into sections. Read-only end to end —
 * the route writes nothing; the ACCEPTED analysis is applied by the caller
 * (`features/sections/apply.ts`) as that user's own undoable edit. Rows are
 * pre-cut client-side to the caps the server also enforces; an oversized
 * request 400s rather than being silently truncated.
 *
 * `model` omitted = the server-resolved default (admin personal > admin
 * global > the provider's configured default).
 */
export function suggestSections(
  boardId: string,
  rows: readonly SectionsSuggestRow[],
  model?: string,
): Promise<SectionsSuggestResponse> {
  return apiFetch<SectionsSuggestResponse>(
    `/api/boards/${encodeURIComponent(boardId)}/sections-suggest`,
    {
      method: "POST",
      body: JSON.stringify({ rows, ...(model === undefined ? {} : { model }) }),
    },
  );
}

/* ------------------------------------------------------------------ */
/* Design theme — art direction (stage one)                            */
/* ------------------------------------------------------------------ */

export interface ArtDirectionResult {
  artDirection: ArtDirection;
  /** Model that actually served the call — stored on the doc as provenance. */
  model: string;
  tokensIn?: number;
  tokensOut?: number;
}

/**
 * Ask the server to write the art direction for this video from a sample of
 * its script. The response is applied to the doc by the CALLER
 * (`session.applyArtDirection`) so it lands as that user's own undoable edit —
 * the route itself writes nothing. Same posture as `suggestArrangeSplit`.
 *
 * The sample is pre-cut client-side (`features/design/sample.ts`) to the same
 * caps the server enforces; an oversized request 400s rather than being
 * silently truncated.
 */
export function generateArtDirection(
  boardId: string,
  scriptSample: readonly string[],
  brief?: string,
): Promise<ArtDirectionResult> {
  return apiFetch<ArtDirectionResult>(
    `/api/boards/${encodeURIComponent(boardId)}/design/art-direction`,
    {
      method: "POST",
      body: JSON.stringify({ scriptSample, ...(brief ? { brief } : {}) }),
    },
  );
}

/* ------------------------------------------------------------------ */
/* Design theme — key visuals (stage two, an async job)                */
/* ------------------------------------------------------------------ */

export interface StartDesignImagesResult {
  jobId: string;
  runId: string;
  total: number;
}

/**
 * Start a style-sheet run — one page per element family (text, the four
 * charts, parallax, animation parts, map).
 *
 * Returns 202 immediately: the pages land in the doc as they finish and
 * progress rides the doc's `imgJob` key, so nothing here polls. The BODY IS
 * EMPTY by design — the page list is the server's fixed catalogue, and the art
 * direction and reference frames are read server-side off the live doc, so a
 * stale tab cannot spend money on anything but what the board says right now.
 * `total` in the response is how many pages the run will render.
 */
export function startDesignImages(boardId: string): Promise<StartDesignImagesResult> {
  return apiFetch<StartDesignImagesResult>(
    `/api/boards/${encodeURIComponent(boardId)}/design/images`,
    { method: "POST", body: "{}" },
  );
}

export interface StartDesignRowImagesResult {
  jobId: string;
  runId: string;
  total: number;
  /** Selected rows with no script text — dropped, not an error. */
  skipped: number;
}

/**
 * Start a per-row design run — one storyboard frame per selected script row.
 *
 * Only IDS travel: the row text, the art direction and the reference frames are
 * all read server-side from the live doc, so the frames illustrate what the
 * script says right now rather than what a stale tab had in memory.
 */
export function startDesignRowImages(
  boardId: string,
  sheetId: string,
  colId: string,
  rowIds: readonly string[],
): Promise<StartDesignRowImagesResult> {
  return apiFetch<StartDesignRowImagesResult>(
    `/api/boards/${encodeURIComponent(boardId)}/design/row-images`,
    { method: "POST", body: JSON.stringify({ sheetId, colId, rowIds }) },
  );
}

/** Ask a run to stop. It halts before the next frame; stored frames stay. */
export function cancelDesignImages(
  boardId: string,
  jobId: string,
): Promise<{ jobId: string; status: string }> {
  return apiFetch<{ jobId: string; status: string }>(
    `/api/boards/${encodeURIComponent(boardId)}/design/images/${encodeURIComponent(jobId)}`,
    { method: "DELETE" },
  );
}

/** Clear a TERMINAL run's banner for everyone (see the route's note on why). */
export function dismissDesignImagesState(boardId: string): Promise<{ cleared: true }> {
  return apiFetch<{ cleared: true }>(
    `/api/boards/${encodeURIComponent(boardId)}/design/images-state`,
    { method: "DELETE" },
  );
}

/* ------------------------------------------------------------------ */
/* Thumbnails (F-130) — five hooks, five frames, then refine any one   */
/* ------------------------------------------------------------------ */

export interface StartThumbnailsResult {
  jobId: string;
  runId: string;
  /** Frames the run sets out to make; the hooks pass may return fewer. */
  total: number;
}

export interface ThumbnailModelPicks {
  imageModel?: string | undefined;
  textModel?: string | undefined;
}

/**
 * Start a thumbnail run. Returns 202 immediately: the hooks are written, then
 * each frame lands in the doc as it finishes, with progress on the doc's
 * `thumbs.job` key — nothing here polls. Only the sheet id and the model
 * picks travel: portraits, words, script, art direction and reference frames
 * are read server-side off the live doc, so a stale tab cannot spend money on
 * anything but what the board says right now.
 */
export function startThumbnails(
  boardId: string,
  sheetId: string,
  picks: ThumbnailModelPicks = {},
): Promise<StartThumbnailsResult> {
  return apiFetch<StartThumbnailsResult>(`/api/boards/${encodeURIComponent(boardId)}/thumbnails`, {
    method: "POST",
    body: JSON.stringify({
      sheetId,
      ...(picks.imageModel ? { imageModel: picks.imageModel } : {}),
      ...(picks.textModel ? { textModel: picks.textModel } : {}),
    }),
  });
}

export interface RefineThumbnailResult {
  /** The new frame, already in the doc under its parent. */
  shot: ThumbShotInfo;
  costUsd?: number;
}

/**
 * Edit ONE thumbnail from feedback. SYNCHRONOUS and slow (tens of seconds —
 * one image call), like the brainstorm visual; the caller shows a pending
 * card rather than a progress bar. The result is written to the doc by the
 * server before it answers, so the gallery updates through sync as well.
 */
export function refineThumbnail(
  boardId: string,
  shotId: string,
  feedback: string,
  imageModel?: string,
): Promise<RefineThumbnailResult> {
  return apiFetch<RefineThumbnailResult>(
    `/api/boards/${encodeURIComponent(boardId)}/thumbnails/${encodeURIComponent(shotId)}/refine`,
    {
      method: "POST",
      body: JSON.stringify({ feedback, ...(imageModel ? { imageModel } : {}) }),
    },
  );
}

/** Ask a run to stop. It halts before the next frame; stored frames stay. */
export function cancelThumbnails(
  boardId: string,
  jobId: string,
): Promise<{ jobId: string; status: string }> {
  return apiFetch<{ jobId: string; status: string }>(
    `/api/boards/${encodeURIComponent(boardId)}/thumbnails/${encodeURIComponent(jobId)}`,
    { method: "DELETE" },
  );
}

/** Clear a TERMINAL run's bar for everyone (see the design route's note on why). */
export function dismissThumbnailsState(boardId: string): Promise<{ cleared: true }> {
  return apiFetch<{ cleared: true }>(
    `/api/boards/${encodeURIComponent(boardId)}/thumbnails-state`,
    { method: "DELETE" },
  );
}

/* ------------------------------------------------------------------ */
/* Script brainstorm — highlight a passage, ask the AI about it        */
/* ------------------------------------------------------------------ */

/** One earlier exchange, replayed so a follow-up has something to follow. */
export interface BrainstormTurn {
  question: string;
  answer: string;
}

/**
 * `POST …/script/brainstorm` request body (packages/server/src/api/script.ts).
 *
 * `script` and `selection` are BOTH sent, and that is the feature: the
 * selection says what the question is about, the script says what the video is.
 * `sampled` is not decoration — it tells the prompt to explain the gaps to the
 * model, which otherwise reads a thinned script as one full of jump cuts.
 * Everything here is pre-cut client-side (`features/script/brainstorm.ts`) to
 * the caps the server also enforces; an oversized request 400s rather than
 * being silently truncated server-side.
 */
export interface BrainstormRequest {
  script: string[];
  sampled?: boolean;
  totalLines?: number;
  selection: string[];
  question: string;
  history?: BrainstormTurn[];
  /**
   * Which model answers THIS question (the panel's picker). Omit for the
   * server-resolved default — for brainstorm, OpenRouter's auto router.
   */
  model?: string;
}

export interface BrainstormResult {
  /** Prose for a person to read — never applied to the board by anything. */
  answer: string;
  /**
   * The model that ACTUALLY answered. Worth surfacing here in a way it is not
   * elsewhere: this function runs on OpenRouter's auto router by default, so
   * the answering model differs from question to question.
   */
  model: string;
  tokensIn?: number;
  tokensOut?: number;
}

/**
 * Ask one question about a highlighted passage. Read-only end to end — the
 * route writes nothing and the answer lands in a popover, so unlike
 * `generateArtDirection` there is nothing for the caller to apply.
 *
 * Rejects with an `ApiError` whose `status`/`code` the caller maps to a message
 * (`brainstormErrorMessage`).
 */
export function askScriptBrainstorm(
  boardId: string,
  request: BrainstormRequest,
): Promise<BrainstormResult> {
  return apiFetch<BrainstormResult>(
    `/api/boards/${encodeURIComponent(boardId)}/script/brainstorm`,
    { method: "POST", body: JSON.stringify(request) },
  );
}

/**
 * `POST /api/boards/:id/agent` — one board-agent turn (F-144). The server
 * reads the live doc and answers with a reply and, when the model proposed
 * edits, a CONCRETE plan the panel applies locally (features/agent/apply.ts).
 * Nothing is written server-side.
 *
 * Rejects with an `ApiError` whose `status`/`code` the panel maps to a
 * message (`agentErrorMessage`).
 */
export function askBoardAgent(boardId: string, request: BoardAgentRequest): Promise<BoardAgentResponse> {
  return apiFetch<BoardAgentResponse>(`/api/boards/${encodeURIComponent(boardId)}/agent`, {
    method: "POST",
    body: JSON.stringify(request),
  });
}

/**
 * `GET /api/ai/models` — the PROVIDER'S catalog. Since the admin-managed model
 * list landed, this feeds the admin add form's suggestions and the `defaults`
 * map only; the pickers themselves render `fetchAiModelList` below.
 *
 * Cached SERVER-side (the adapter holds one list for ten minutes), so calling
 * this each time a picker opens costs a local round trip, not a third-party
 * one. `models` comes back empty for a provider with no catalog; a caller must
 * render that as "no suggestions", never as "no models".
 *
 * Rejects with an `ApiError`: 503 `ai-not-configured` when no key is set,
 * 502 `catalog-unavailable` when the provider's own call failed.
 */
export function listAiModels(): Promise<AiModelCatalogResponse> {
  return apiFetch<AiModelCatalogResponse>("/api/ai/models");
}

/* ------------------------------------------------------------------ */
/* The admin-managed AI model list — what every dropdown renders       */
/* ------------------------------------------------------------------ */

/** `GET /api/ai/model-list` — open to any signed-in user; everyone picks. */
export function fetchAiModelList(): Promise<AiModelListResponse> {
  return apiFetch<AiModelListResponse>("/api/ai/model-list");
}

export function createAiModelEntry(input: AiModelEntryInput): Promise<AiModelEntry> {
  return apiFetch<AiModelEntry>("/api/admin/ai-models", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function updateAiModelEntry(
  id: string,
  patch: Partial<AiModelEntryInput>,
): Promise<AiModelEntry> {
  return apiFetch<AiModelEntry>(`/api/admin/ai-models/${encodeURIComponent(id)}`, {
    method: "PUT",
    body: JSON.stringify(patch),
  });
}

export function deleteAiModelEntry(id: string): Promise<AiModelEntryDeletedResponse> {
  return apiFetch<AiModelEntryDeletedResponse>(`/api/admin/ai-models/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

/* ------------------------------------------------------------------ */
/* Presenter photo library (F-134) — the faces thumbnail runs pick from */
/* ------------------------------------------------------------------ */

/** `GET /api/presenter-photos` — open to any signed-in user; the panel shows it. */
export function fetchPresenterPhotos(): Promise<PresenterPhotosResponse> {
  return apiFetch<PresenterPhotosResponse>("/api/presenter-photos");
}

/**
 * Upload ONE photo of the presenter — anyone who edits boards may. Raw
 * bytes, like `uploadAsset`; the server sniffs the type, stores the photo
 * and TAGS it before answering (a few seconds — one vision call), so the
 * answer normally carries the description. `tagError` names the reason when
 * it does not; `tagPresenterPhoto` retries.
 */
export async function uploadPresenterPhoto(file: Blob): Promise<PresenterPhotoUploadResponse> {
  const contentType = file.type.startsWith("image/") ? file.type : "image/png";
  const response = await authedFetch("/api/presenter-photos", {
    method: "POST",
    headers: { "content-type": contentType },
    body: file,
  });
  if (!response.ok) {
    let code = "";
    let body: unknown;
    try {
      body = await response.json();
      code = (body as { error?: string }).error ?? "";
    } catch {
      // non-JSON error body — status alone is enough
    }
    throw new ApiError(
      `POST /api/presenter-photos failed (${response.status}${code ? `: ${code}` : ""})`,
      response.status,
      code,
      body,
    );
  }
  return (await response.json()) as PresenterPhotoUploadResponse;
}

/**
 * Re-run the tagging pass on one photo — the retry after an upload whose
 * tagging failed, or a re-read on another model. ONE vision call,
 * synchronous. The body is always an object: the route validates a schema.
 */
export function tagPresenterPhoto(id: string, model?: string): Promise<PresenterPhoto> {
  return apiFetch<PresenterPhoto>(`/api/presenter-photos/${encodeURIComponent(id)}/tag`, {
    method: "POST",
    body: JSON.stringify(model ? { model } : {}),
  });
}

/** An admin's correction of the description or the offer — nothing removes a photo. */
export function updatePresenterPhoto(
  id: string,
  patch: PresenterPhotoUpdateInput,
): Promise<PresenterPhoto> {
  return apiFetch<PresenterPhoto>(`/api/admin/presenter-photos/${encodeURIComponent(id)}`, {
    method: "PUT",
    body: JSON.stringify(patch),
  });
}

/** `POST …/script/brainstorm-visual` request body. */
export interface BrainstormVisualRequest {
  /** The question the answer replied to. */
  question: string;
  /** The answer being illustrated — the visual's brief. */
  answer: string;
  /** The highlighted lines the exchange was about. */
  selection: string[];
}

export interface BrainstormVisualResult {
  /** The stored frame — render it with `assetUrl(assetId)`. */
  assetId: string;
  w: number;
  h: number;
  /** The image model that rendered it. */
  model: string;
  /** True when the board's design theme shaped the frame. */
  themed: boolean;
  costUsd?: number;
}

/**
 * Render ONE frame from a brainstorm answer. SYNCHRONOUS and slow — image
 * generation is tens of seconds, so the caller shows a pending state rather
 * than a progress bar (there is no job to watch; the answer IS the response).
 * The board's art direction and reference frames are read server-side off the
 * live doc, which is what makes the frame theme-matched when a theme exists.
 */
export function generateBrainstormVisual(
  boardId: string,
  request: BrainstormVisualRequest,
): Promise<BrainstormVisualResult> {
  return apiFetch<BrainstormVisualResult>(
    `/api/boards/${encodeURIComponent(boardId)}/script/brainstorm-visual`,
    { method: "POST", body: JSON.stringify(request) },
  );
}

/** `POST …/script/brainstorm-translate` request body. */
export interface BrainstormTranslateRequest {
  /** The answer to translate, as the popover shows it. */
  text: string;
  /** BCP-47 target language tag (features/translate/languages.ts). */
  to: string;
}

export interface BrainstormTranslateResult {
  /** The answer in the target language — read, never applied to the board. */
  text: string;
  /** The model that served the call (the translate-column model, server-side). */
  model: string;
  tokensIn?: number;
  tokensOut?: number;
}

/**
 * Render ONE brainstorm answer in another language. A plain translation on
 * the server's translate model, not a brainstorm follow-up (it does not
 * re-send the script), and read-only like the rest of this feature.
 *
 * Rejects with an `ApiError` whose `status`/`code` the caller maps to a
 * message (`translateErrorMessage`).
 */
export function translateBrainstormAnswer(
  boardId: string,
  request: BrainstormTranslateRequest,
): Promise<BrainstormTranslateResult> {
  return apiFetch<BrainstormTranslateResult>(
    `/api/boards/${encodeURIComponent(boardId)}/script/brainstorm-translate`,
    { method: "POST", body: JSON.stringify(request) },
  );
}

/* ------------------------------------------------------------------ */
/* Translate column (F-70, docs/02 §G)                                 */
/* ------------------------------------------------------------------ */

/**
 * POST /api/boards/:id/translate request body. The SOURCE is deliberately
 * absent: it is always the board's locked "Original Script" column, which the
 * server resolves from the doc (F-28 — there is no source picker, and a client
 * could not be trusted to name it anyway).
 */
export interface TranslateColumnRequest {
  sheetId: string;
  /** The column the translation is written INTO. */
  targetColId: string;
  /** Source language tag, or the shared `TRANSLATION_AUTO_LANG` ("auto"). */
  from: string;
  to: string;
  /** Optional per-mapping model override; omitted = the server's default. */
  model?: string;
  /**
   * F-70.2's "Re-translate": also re-send rows that already hold a CURRENT
   * machine translation. DEFAULT (absent/false) IS RESUME — only the rows that
   * still need it — which is what makes a re-run after a cancel or a crash
   * continue rather than re-buy the whole column ("resumable by construction").
   * So a plain start on an already-translated column is a no-op
   * (`translated: 0`), NOT a re-translate; the dialog must send `true` for an
   * explicit one. A human-edited target is never re-sent either way (C5.1).
   */
  retranslate?: boolean;
}

/**
 * A finished run's full breakdown — the body of `GET …/translate/:jobId`, NOT
 * of the start call (which returns 202 the moment the job is accepted, long
 * before any of this is known).
 *
 * This is deliberately NOT on the board doc: the doc carries what a progress
 * bar needs (`done`/`total`/`failed`), and putting the breakdown there would
 * make every client re-sync it on every tick to show something only the person
 * who ran it reads.
 *
 * Counter semantics (they drive `resultSummary`'s wording, so they are worth
 * stating precisely):
 *  - `translated` — rows written; those cells are now `st:"mt"`.
 *  - `skipped` — nothing written and nothing wrong: an empty source row (never
 *    sent to the model), a row that already held a current translation (the
 *    resume default — see `retranslate`), or a C5 guard refusal (a human owns
 *    the target, the row was deleted mid-flight, or the source changed
 *    mid-flight — that last one leaves the target `st:"outdated"`).
 *  - `failed` — the batch could not be trusted (row alignment mismatch after a
 *    retry, or the provider kept failing). Retryable by re-running.
 * The doc's `done` counter is `translated + skipped + failed`.
 * `tokensIn`/`tokensOut` are omitted entirely when the provider reported no
 * usage; when present they are what the F-70.7 toast reports as the run's cost.
 */
export interface TranslateColumnResult {
  translated: number;
  skipped: number;
  failed: number;
  tokensIn?: number;
  tokensOut?: number;
  /**
   * Set when the run STOPPED EARLY with rows still pending (counted in
   * `skipped`): `"deadline"` = it hit the server's per-run wall-clock budget;
   * `"aborted"` = the request was abandoned. Absent on a complete run. Either
   * way the fix is the same — run it again to continue.
   */
  stopped?: "deadline" | "aborted";
}

/** Lifecycle of a job in the server's registry (`GET …/translate/:jobId`). */
export type TranslateJobStatus = "running" | "done" | "cancelled" | "error";

/** `POST …/translate` 202 body — the run has STARTED, not finished. */
export interface TranslateStartResponse {
  /** Handle for cancel/readback, and the `jobId` on the doc's `txJob` state. */
  jobId: string;
  /**
   * The progress DENOMINATOR: every row on the sheet, because every row is
   * decided (an empty source row is skipped, and skipping is deciding). NOT
   * the estimate's `rows`, which counts only the rows that cost tokens. Lets
   * the bar render 0/total before the first doc tick arrives.
   */
  total: number;
}

/** `GET …/translate/:jobId` body — the OUTCOME (progress lives on the doc). */
export interface TranslateJobResponse {
  jobId: string;
  status: TranslateJobStatus;
  startedAt: number;
  finishedAt?: number;
  /** The full breakdown — present once the job finished without throwing. */
  result?: TranslateColumnResult;
  /** Failure detail — present on `status: "error"`. */
  error?: string;
}

/** `DELETE …/translate/:jobId` body. */
export interface TranslateCancelResponse {
  jobId: string;
  /** `"cancelling"` = the run stops after its in-flight batch (202). */
  status: TranslateJobStatus | "cancelling";
}

/** `DELETE …/translate/state` body — the finished run's state is off the doc. */
export interface TranslateDismissResponse {
  jobId: string;
  dismissed: true;
}

/** `GET …/translate/estimate` body — the F-70 cost guard's numbers. */
export interface TranslateEstimate {
  /**
   * NON-EMPTY source rows that still need translating — the rows that cost
   * tokens, and the number the ~500-row confirmation threshold compares
   * against. Not the job's `total` (which counts every row on the sheet).
   */
  rows: number;
  approxTokensIn: number;
  approxTokensOut: number;
  /** Omitted when the model has no published price — never guessed. */
  approxUsd?: number;
  /** The model that would actually run. */
  model: string;
  /**
   * The server's per-run row cap. The estimate is sized WITHOUT it (so it can
   * report how big the run really is), which means `rows` may exceed it and the
   * start POST would 400 `too-many-rows` — see `overRowCap`.
   */
  maxRows?: number;
}

/**
 * START translating the Original Script column into `targetColId` (F-70,
 * async since 2026-07-16). Resolves with `{jobId, total}` as soon as the run is
 * ACCEPTED (202) — not when it finishes: a 10k-row script is ~250 model calls
 * over many minutes, so the old synchronous POST hung the browser and died on
 * any network blip.
 *
 * Everything after this call arrives on its own:
 *  - PROGRESS is published to the board doc (`txJob`) by the job and read with
 *    `board.getTranslationJob()` / `observeTranslationJob()` — which is why
 *    every client shares one bar and a mid-job reload still shows it.
 *  - The CELLS arrive over the normal sync channel as the server's C5 guarded
 *    writers fill them in. This call never writes a cell.
 *
 * Rejects with an `ApiError` whose `status`/`code` the caller maps to a message
 * (`translateErrorMessage`): 503 translation-not-configured, 409
 * already-running (`body.jobId` names the live run), 403 forbidden, 404
 * not-found, 429 rate-limited | too-many-concurrent, 400 unknown-sheet |
 * unknown-column | no-source-column | target-is-source | unknown-language |
 * model-not-allowed | too-many-rows.
 */
export function startTranslateColumn(
  boardId: string,
  request: TranslateColumnRequest,
): Promise<TranslateStartResponse> {
  return apiFetch<TranslateStartResponse>(
    `/api/boards/${encodeURIComponent(boardId)}/translate`,
    { method: "POST", body: JSON.stringify(request) },
  );
}

/**
 * CANCEL a running job (F-70). 202 = it stops after its in-flight batch (those
 * tokens are already spent, so that batch's answer is still worth writing); the
 * doc's `txJob` flips to `cancelled` when it actually does, which is what the
 * progress bar watches. Cancelling a finished job is a no-op, not an error.
 *
 * This is the ONLY way to stop a run: closing the tab deliberately does not,
 * because surviving a closed tab is the point of the job.
 */
export function cancelTranslateColumn(
  boardId: string,
  jobId: string,
): Promise<TranslateCancelResponse> {
  return apiFetch<TranslateCancelResponse>(
    `/api/boards/${encodeURIComponent(boardId)}/translate/${encodeURIComponent(jobId)}`,
    { method: "DELETE" },
  );
}

/**
 * DISMISS a finished run's state (F-70). Terminal `txJob` state stays on the doc
 * so the outcome can be read — which means it is on the board for EVERYONE until
 * something clears it, and a local "dismissed" flag would bring it back on the
 * next reload, for every user, forever.
 *
 * So the dismissal is shared, like the bar it removes. Server-side because
 * `txJob` is server-written by discipline (schema.ts), and board-scoped rather
 * than keyed on the job registry, which forgets a job on restart — exactly the
 * case that leaves an "interrupted" banner needing dismissal.
 *
 * 409 `job-not-dismissable` = the state moved on (a new run started, or this one
 * is running): the doc already says so, so nothing to report.
 */
export function dismissTranslationJob(
  boardId: string,
  jobId: string,
): Promise<TranslateDismissResponse> {
  return apiFetch<TranslateDismissResponse>(
    `/api/boards/${encodeURIComponent(boardId)}/translate/state?jobId=${encodeURIComponent(jobId)}`,
    { method: "DELETE" },
  );
}

/**
 * The job's OUTCOME and what it cost. Deliberately not on the doc: the doc
 * carries what a progress bar needs (done/total/failed) and would otherwise
 * make every client re-sync the whole breakdown on every tick.
 *
 * 404 `unknown-job` is expected rather than exceptional — the registry is
 * in-memory, so a server restart forgets finished jobs. Callers fall back to
 * the doc's counters.
 */
export function getTranslateJob(boardId: string, jobId: string): Promise<TranslateJobResponse> {
  return apiFetch<TranslateJobResponse>(
    `/api/boards/${encodeURIComponent(boardId)}/translate/${encodeURIComponent(jobId)}`,
  );
}

/**
 * Size a run before starting it (F-70 cost guard). Pure server-side plan +
 * arithmetic — no provider call, so asking is free. `retranslate` must match
 * what the start request will send, or the estimate quotes a different run than
 * the one that executes.
 */
export function estimateTranslateColumn(
  boardId: string,
  query: {
    sheetId: string;
    targetColId: string;
    from?: string;
    to?: string;
    retranslate?: boolean;
  },
): Promise<TranslateEstimate> {
  const params = new URLSearchParams({
    sheetId: query.sheetId,
    targetColId: query.targetColId,
  });
  if (query.from !== undefined) params.set("from", query.from);
  if (query.to !== undefined) params.set("to", query.to);
  // The route's schema takes the literal "true"/"false" — query strings carry
  // no booleans.
  if (query.retranslate !== undefined) params.set("retranslate", String(query.retranslate));
  return apiFetch<TranslateEstimate>(
    `/api/boards/${encodeURIComponent(boardId)}/translate/estimate?${params.toString()}`,
  );
}

/* ------------------------------------------------------------------ */
/* Admin: AI model settings (per-function "apply only me" / "apply to */
/* everyone" — packages/server/src/api/admin-ai-settings.ts). The     */
/* response DTOs come from @kola/shared, so these `apiFetch<T>` casts */
/* name the SAME declarations the server's handlers satisfy.          */
/* ------------------------------------------------------------------ */

/** GET — one entry per AI function; 403s for anyone but `role: "admin"`. */
export function fetchAiModelSettings(): Promise<AiModelSettingResponse[]> {
  return apiFetch<AiModelSettingResponse[]>("/api/admin/ai-model-settings");
}

/** "Apply to everyone" — persisted, affects every user's model-less runs. */
export function setGlobalAiModel(
  functionKey: AiFunctionKey,
  model: string,
): Promise<AiModelSettingResponse> {
  return apiFetch<AiModelSettingResponse>(
    `/api/admin/ai-model-settings/${encodeURIComponent(functionKey)}/global`,
    { method: "POST", body: JSON.stringify({ model }) },
  );
}

/** Reverts a function to the server's config default. */
export function clearGlobalAiModel(
  functionKey: AiFunctionKey,
): Promise<AiModelSettingClearedResponse> {
  return apiFetch<AiModelSettingClearedResponse>(
    `/api/admin/ai-model-settings/${encodeURIComponent(functionKey)}/global`,
    { method: "DELETE" },
  );
}

/** "Apply only me" — in-memory, affects only the CALLING admin's own runs. */
export function setPersonalAiModel(
  functionKey: AiFunctionKey,
  model: string,
): Promise<AiModelPersonalSettingResponse> {
  return apiFetch<AiModelPersonalSettingResponse>(
    `/api/admin/ai-model-settings/${encodeURIComponent(functionKey)}/personal`,
    { method: "POST", body: JSON.stringify({ model }) },
  );
}

/** Stops testing — reverts this admin back to the global/config default. */
export function clearPersonalAiModel(
  functionKey: AiFunctionKey,
): Promise<AiModelSettingClearedResponse> {
  return apiFetch<AiModelSettingClearedResponse>(
    `/api/admin/ai-model-settings/${encodeURIComponent(functionKey)}/personal`,
    { method: "DELETE" },
  );
}

/* ------------------------------------------------------------------ */
/* Debug reports (core/debug-log.ts → api/debug-reports.ts)            */
/* ------------------------------------------------------------------ */

/** Send this tab's debug log to the project owner. */
export function sendDebugReport(body: DebugReportRequest): Promise<CreateDebugReportResponse> {
  return apiFetch<CreateDebugReportResponse>("/api/debug-reports", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/** Admin only: the reports users have sent, newest first. */
export function fetchDebugReports(limit = 50): Promise<DebugReportsResponse> {
  return apiFetch<DebugReportsResponse>(`/api/admin/debug-reports?limit=${limit}`);
}

/** Admin only: dismiss a report. */
export function deleteDebugReport(id: string): Promise<DeleteDebugReportResponse> {
  return apiFetch<DeleteDebugReportResponse>(`/api/admin/debug-reports/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

/* ------------------------------------------------------------------ */
/* Script line kinds (F-17-type, admin-defined `/` kinds) —            */
/* packages/server/src/api/text-kinds.ts. Read by every user (cells    */
/* store only a kind's id), written by `kinds.manage`.                 */
/* ------------------------------------------------------------------ */

/** GET — every kind, in creation order (the `/` menu's order). */
export function fetchTextKinds(): Promise<TextKindsResponse> {
  return apiFetch<TextKindsResponse>("/api/text-kinds");
}

export function createTextKind(input: TextKindInput): Promise<TextKind> {
  return apiFetch<TextKind>("/api/admin/text-kinds", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function updateTextKind(id: string, patch: Partial<TextKindInput>): Promise<TextKind> {
  return apiFetch<TextKind>(`/api/admin/text-kinds/${encodeURIComponent(id)}`, {
    method: "PUT",
    body: JSON.stringify(patch),
  });
}

export function deleteTextKind(id: string): Promise<TextKindDeletedResponse> {
  return apiFetch<TextKindDeletedResponse>(`/api/admin/text-kinds/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

/* ------------------------------------------------------------------ */
/* Translate cells (the toolbar's "translate this cell", 2026-09-04)   */
/* ------------------------------------------------------------------ */

/** `POST …/translate/cells` body — see the server route for the caps. */
export interface TranslateCellsRequest {
  to: string;
  /** Source tag, or "auto" (the default when absent). */
  from?: string;
  /** `id` is the caller's key (`rowId:colId`); the answer is keyed the same way. */
  cells: { id: string; text: string }[];
}

export interface TranslateCellsResult {
  /** `id → translation`, exactly the ids that were sent. */
  translations: Record<string, string>;
  model: string;
  tokensIn?: number;
  tokensOut?: number;
}

/**
 * Translate a handful of cells in ONE model call, answered in the request —
 * no job, no doc write. The caller appends each answer under its cell's own
 * text under the user's origin, so the result is theirs to undo.
 *
 * Rejects with an `ApiError` (`translateErrorMessage` maps the codes).
 */
export function translateCells(
  boardId: string,
  request: TranslateCellsRequest,
): Promise<TranslateCellsResult> {
  return apiFetch<TranslateCellsResult>(
    `/api/boards/${encodeURIComponent(boardId)}/translate/cells`,
    { method: "POST", body: JSON.stringify(request) },
  );
}
